<template>
  <div
    ref="specsRoot"
    @click.capture="stampFrcSpecButtonId"
    @click="onSpecsClick"
  >
    <ProductSpecs
      :groups="productSpecsGroups"
      :lightbox-gallery="lightboxGallery"
    />
  </div>
  <div
    v-if="store.decoratedProduct?.productDocumentationLink"
    class="product-specs"
  >
    <div class="product-specs__group">
      <header class="product-specs__group-header">
        <h4
          :class="['product-specs__group-headline', $style.prodSpecGroupHead]"
        >
          {{
            $textDictionary['pdp.specifications.headline.label'] ??
            'documentation_available'
          }}
        </h4>
      </header>
      <div
        :class="['product-specs__group-content', $style.prodSpecGroupoContent]"
      >
        <HyperLink v-bind="documentationLink">
          {{
            $textDictionary['pdp.specifications.link.label'] ??
            'documentation_url'
          }}
        </HyperLink>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { HyperLink, ProductSpecs } from '@hypercodestudio/basler-components';
import type {
  HyperlinkInterface,
  LightboxGalleryInterface,
  SpecInlineLink
} from '@hypercodestudio/basler-components';

import { useProductDetailPageStore } from '~/stores/productDetailPageStore/productDetailPageStore';
import { useFrameRateCalculatorPdpLink } from '~/composables/useFrameRateCalculatorPdpLink';
import {
  sensorComparerOpenEvent,
  useSensorComparerEligibility,
  useSensorComparerHref
} from '~/composables/useSensorComparerEligibility';
import { ensureFrcSpecButtonId } from '~/utils/shop/ensureFrcSpecButtonId';
import { SENSOR_ATTRIBUTE_GROUP_CODE } from '~/utils/shop/specTableValueWithLink';

const store = useProductDetailPageStore();
const { $textDictionary, $analytics } = useNuxtApp();
const specsRoot = ref<HTMLElement | null>(null);

function stampFrcSpecButtonId() {
  ensureFrcSpecButtonId(specsRoot.value);
}

onMounted(stampFrcSpecButtonId);
onUpdated(stampFrcSpecButtonId);

const decoratedProduct = computed(() => store.decoratedProduct);
const { link: frcPdpLink } = useFrameRateCalculatorPdpLink(decoratedProduct);

const productSku = computed(() => decoratedProduct.value?.sku?.trim() ?? '');
const requestedSkus = computed(() =>
  productSku.value ? [productSku.value] : []
);
const { eligibleSkus } = useSensorComparerEligibility(requestedSkus);
const { href: sensorComparerHref } = useSensorComparerHref(eligibleSkus);

const frcInlineLink = computed(() => {
  if (!frcPdpLink.value.visible) {
    return undefined;
  }
  return {
    href: frcPdpLink.value.href,
    label: frcPdpLink.value.label,
    tooltip: frcPdpLink.value.tooltip,
    testId: 'pdp-frc-link'
  };
});

const sensorComparerHeaderLink = computed((): SpecInlineLink | undefined => {
  if (eligibleSkus.value.length === 0) {
    return undefined;
  }
  return {
    href: sensorComparerHref.value,
    label:
      $textDictionary.value?.['pdp.sensorComparer.linkText'] ??
      'Compare sensor',
    tooltip:
      $textDictionary.value?.['pdp.sensorComparer.linkTooltip'] ??
      'Open the Sensor Comparer for this camera',
    testId: 'pdp-sensor-comparer-link'
  };
});

const isChartJsReady = useChartJsReady();

function hideChartGroupsUntilChartJsIsLoaded<Group extends { type: string }>(
  groups: Group[]
): Group[] {
  return isChartJsReady.value
    ? groups
    : groups.filter((group) => group.type !== 'charts');
}

const productSpecsGroups = computed(() => {
  const groups = hideChartGroupsUntilChartJsIsLoaded(
    store.decoratedProduct?.getProductSpecifications(frcInlineLink.value) ?? []
  );
  const headerLink = sensorComparerHeaderLink.value;
  if (!headerLink) {
    return groups;
  }
  return groups.map((group) =>
    group.code === SENSOR_ATTRIBUTE_GROUP_CODE
      ? { ...group, headerLink }
      : group
  );
});

/** Same `customEvent` shape as the other FRC analytics (see useFrameRateCalculatorAnalytics). */
function frcSpecLinkClickEvent() {
  return {
    event: 'customEvent',
    eventInfo: {
      category: 'FrameRateCalculator',
      action: 'click',
      label: 'pdp_spec_link'
    }
  };
}

function onSpecsClick(event: MouseEvent) {
  const target = event.target;
  if (!(target instanceof Element)) {
    return;
  }
  if (target.closest('[data-testid="pdp-frc-link"]')) {
    $analytics?.pushToDataLayer(frcSpecLinkClickEvent());
    return;
  }
  if (!target.closest('[data-testid="pdp-sensor-comparer-link"]')) {
    return;
  }
  $analytics?.pushToDataLayer(
    sensorComparerOpenEvent('openFromPdp', productSku.value)
  );
}

const documentationLink = computed((): HyperlinkInterface => ({
  uri: store.decoratedProduct?.productDocumentationLink ?? '',
  external: true,
  target: '_blank'
}));

const lightboxGallery = computed<LightboxGalleryInterface | undefined>(() => {
  if (!store.decoratedProduct) {
    return undefined;
  }

  return {
    title:
      $textDictionary.value['media.gallery.title'] ??
      'Gallery of all media elements',
    isOpen: false,
    triggerBtnLabel:
      $textDictionary.value['media.gallery.trigger.btn.label'] ??
      'Open media in lightbox',
    nextBtnLabel:
      $textDictionary.value['media.gallery.next.btn.label'] ?? 'Next media',
    prevBtnLabel:
      $textDictionary.value['media.gallery.prev.btn.label'] ?? 'Previous media',
    closeBtnLabel:
      $textDictionary.value['media.gallery.close.btn.label'] ??
      'Close lightbox',
    mediaList:
      store.decoratedProduct?.getProductSpecificationsLightboxGallery() ?? []
  };
});
</script>

<style module>
.prodSpecGroupHead {
  margin: 0;
}

.prodSpecGroupoContent {
  line-height: 2.25;
  font-size: 1rem;
}
@media (max-width: 768px) {
  .product-specs__group {
    margin-block-end: var(--spacing-size-small);
  }
}
</style>
